// This file helps inject environment variables during SSR
import { Injectable, makeStateKey, StateKey, TransferState } from '@angular/core';
import { environment as devEnvironment } from './environment';
import { environment as stagingEnvironment } from './environment.staging';
import { environment as prodEnvironment } from './environment.prod';

// Create a state key for transferring environment
export const ENV_KEY = makeStateKey<any>('environment');

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  // This will hold our environment variables
  private _environment: any;

  constructor(private transferState: TransferState) {
    // Determine environment
    const isBrowser = typeof window !== 'undefined';
    const isServer = !isBrowser;

    // Get environment type - only access process.env on server
    let envType = 'production'; // Default to production for safety

    if (isServer && typeof process !== 'undefined' && process.env) {
      envType = process.env['NODE_ENV'] || 'production';
    }

    // Select base environment based on environment type
    let baseEnv;
    switch (envType) {
      case 'production':
        baseEnv = prodEnvironment;
        break;
      case 'staging':
        baseEnv = stagingEnvironment;
        break;
      default:
        baseEnv = devEnvironment;
    }

    if (isBrowser) {
      // On the browser side, try to get state transferred from server
      const transferredEnv = this.transferState.get(ENV_KEY, null);
      if (transferredEnv) {
        this._environment = transferredEnv;
      } else {
        // Fallback to base env if no transferred state
        this._environment = baseEnv;
      }
    } else {
      // On server side, we can safely access process.env
      // This is where we'll populate environment from Vercel env variables
      const serverEnv = { ...baseEnv };

      // Server-side only: read process.env values
      // All variables: env var overrides if provided, otherwise falls back to environment.ts
      if (isServer && typeof process !== 'undefined' && process.env) {
        // API URLs - override from env vars if provided
        if (process.env['API_URL']) serverEnv.apiUrl = process.env['API_URL'];
        if (process.env['OPEN_API_URL']) serverEnv.openApiUrl = process.env['OPEN_API_URL'];
        if (process.env['DASHBOARD_URL']) serverEnv.dashboardUrl = process.env['DASHBOARD_URL'];

        // Google Maps API Key - override from env var if provided
        if (process.env['GOOGLE_MAPS_API_KEY']) {
          serverEnv.googleMapsApiKey = process.env['GOOGLE_MAPS_API_KEY'];
        }

        // Firebase settings - override from env vars if provided
        if (process.env['FIREBASE_API_KEY']) serverEnv.firebase.apiKey = process.env['FIREBASE_API_KEY'];
        if (process.env['FIREBASE_AUTH_DOMAIN']) serverEnv.firebase.authDomain = process.env['FIREBASE_AUTH_DOMAIN'];
        if (process.env['FIREBASE_PROJECT_ID']) serverEnv.firebase.projectId = process.env['FIREBASE_PROJECT_ID'];
        if (process.env['FIREBASE_STORAGE_BUCKET']) serverEnv.firebase.storageBucket = process.env['FIREBASE_STORAGE_BUCKET'];
        if (process.env['FIREBASE_MESSAGING_SENDER_ID']) serverEnv.firebase.messagingSenderId = process.env['FIREBASE_MESSAGING_SENDER_ID'];
        if (process.env['FIREBASE_APP_ID']) serverEnv.firebase.appId = process.env['FIREBASE_APP_ID'];

        // Validate required fields are not empty (from either env vars or environment.ts).
        // For prod/staging, every Firebase field MUST be set — silent fallback to
        // dev values is a security issue (SAM-005 / SAM-010).
        const requireString = (value: unknown, fieldLabel: string, envVar: string) => {
          if (typeof value !== 'string' || value.trim() === '') {
            throw new Error(`${fieldLabel} (env: ${envVar}) is required but not set for environment "${envType}"`);
          }
        };

        requireString(serverEnv.apiUrl, 'apiUrl', 'API_URL');
        requireString(serverEnv.openApiUrl, 'openApiUrl', 'OPEN_API_URL');
        requireString(serverEnv.dashboardUrl, 'dashboardUrl', 'DASHBOARD_URL');
        requireString(serverEnv.googleMapsApiKey, 'googleMapsApiKey', 'GOOGLE_MAPS_API_KEY');

        if (envType === 'production' || envType === 'staging') {
          requireString(serverEnv.firebase?.apiKey, 'firebase.apiKey', 'FIREBASE_API_KEY');
          requireString(serverEnv.firebase?.authDomain, 'firebase.authDomain', 'FIREBASE_AUTH_DOMAIN');
          requireString(serverEnv.firebase?.projectId, 'firebase.projectId', 'FIREBASE_PROJECT_ID');
          requireString(serverEnv.firebase?.storageBucket, 'firebase.storageBucket', 'FIREBASE_STORAGE_BUCKET');
          requireString(serverEnv.firebase?.messagingSenderId, 'firebase.messagingSenderId', 'FIREBASE_MESSAGING_SENDER_ID');
          requireString(serverEnv.firebase?.appId, 'firebase.appId', 'FIREBASE_APP_ID');

          // Guard against dev Firebase leaking into prod/staging builds.
          if (serverEnv.firebase.projectId.includes('-dev')) {
            throw new Error(`Firebase projectId "${serverEnv.firebase.projectId}" looks like a dev project but environment is "${envType}". Refusing to start.`);
          }
        }
      }

      // Save the environment to transfer state for browser hydration
      this.transferState.set(ENV_KEY, serverEnv);
      this._environment = serverEnv;
    }
  }

  // Getter for environment variables
  get environment(): any {
    return this._environment;
  }
}
